import { useContext } from 'react'
import moment from 'moment'
import { PDFDocument } from 'pdf-lib'
import { isIOS } from 'react-device-detect'
import { saveAs } from 'file-saver'
import { Buffer } from 'buffer'

import { DocumentsContext, UserContext, NotificationContext, LoaderContext, SectionContext } from '../context'
import { fetch_document_data } from '../helpers/documents'
import { areSectionConditionsMet, isCoverPageVariable, EVENT_TYPES, base64toBlob, filteredDocumentValues } from '../utils'
import { log_event } from '../services/analytics'
import { sendEmail } from '../services/functions'
import { baseUrl, download_shared_document, post_request, push_to_immocloud } from '../services/firestore'
import { CONTACT_DATA_KEYS, CONTACT_ID_VARIABLES, DOC_CONTACT_PREFIXES, DOC_CONTACTS_KEYS } from '../constants'
import immoDocsLogo from '../assets/images/immodocs-logo.png'

const eventTypeForAction = (action) => {
  switch(action) {
    case 'preview':
      return EVENT_TYPES.DOCUMENT_PREVIEW
    case 'download':
      return EVENT_TYPES.DOCUMENT_DOWNLOAD
    case 'push_binary':
      return EVENT_TYPES.IMMOCLOUD_UPLOAD
    case 'share':
      return EVENT_TYPES.DOCUMENT_SHARE
    default:
      return ''
  }
}

const useSingleDocumentActions = () => {
  const { getTemplateById, updateDocument, createDocument, temporarilyDeleteMultipleDocuments, getSingleTemplate, clauses, refreshDocument } = useContext(DocumentsContext)
  const { agency, user, partner, setContacts, integration } = useContext(UserContext)
  const { setNotification } = useContext(NotificationContext)
  const { setShowGlobalResponseLoader, setGlobalResponseLoaderText } = useContext(LoaderContext)
  const { sections } = useContext(SectionContext)

  // Get document history
  const getDocumentHistory = (history) => {
    let daysMap = {}
    let daysArray = []
    const dayFormat = 'DD/MM/YYYY'

    for(let key in history) {
      let version = history[key]
      let dayValue = moment(version.created).format(dayFormat)
      if(!daysMap[dayValue]) {
        daysMap[dayValue] = {
          dayValue: dayValue,
          versions: []
        }
      }
      daysMap[dayValue].versions.push(version)
    }

    for(let d in daysMap) {
      daysMap[d].versions.sort((a, b) => {
        if(a.created > b.created) return 1
        else if(a.created < b.created) return -1
        return 0
      })
      daysArray.push(daysMap[d])
    }

    daysArray.sort((a, b) => {
      if(moment(a.dayValue, dayFormat).valueOf() > moment(b.dayValue, dayFormat).valueOf()) return -1
      else if(moment(a.dayValue, dayFormat).valueOf() < moment(b.dayValue, dayFormat).valueOf()) return 1
      return 0
    })

    return daysArray
  }

  const getDocumentEventsDayArray = (events) => {
    let daysMap = {}
    let daysArray = []
    const dayFormat = 'DD/MM/YYYY'

    for(let event of events) {
      let dayValue = moment(event.created).format(dayFormat)
      if(!daysMap[dayValue]) {
        daysMap[dayValue] = {
          dayValue: dayValue,
          events: []
        }
      }
      daysMap[dayValue].events.push(event)
    }

    for(let d in daysMap) {
      daysMap[d].events.sort((a, b) => {
        if(a.created > b.created) return 1
        else if(a.created < b.created) return -1
        return 0
      })
      daysArray.push(daysMap[d])
    }

    daysArray.sort((a, b) => {
      if(moment(a.dayValue, dayFormat).valueOf() > moment(b.dayValue, dayFormat).valueOf()) return -1
      else if(moment(a.dayValue, dayFormat).valueOf() < moment(b.dayValue, dayFormat).valueOf()) return 1
      return 0
    })

    return daysArray
  }


  // Get data for preview
  const getPreviewData = async (doc, template = null, addWatermark = false, options = {}) => {
    options.clauses = clauses
    let events = [eventTypeForAction('preview')]
    log_event(events, partner)
    try {
      let tmplt
      if(template) tmplt = template
      if(!tmplt) tmplt = getTemplateById(doc.template)
      if(!tmplt.sections) {
        tmplt = await getSingleTemplate(doc.template, true)
      }
      if(tmplt && Object.keys(tmplt).length > 0) {
        let documentData = await fetch_document_data(tmplt, doc, 'pdf', agency, user, options, integration)
        if(!documentData) throw new Error('No data')

        let base64Data = `data:application/pdf;base64,${documentData}`
        // if(addWatermark) {
        //   const pdfDoc = await PDFDocument.load(`data:application/pdf;base64,${documentData}`)

        //   const pages = pdfDoc.getPages()

        //   for(let i = 0; i < pages.length; i++) {
        //     const page = pages[i]
        //     const { width, height } = page.getSize()
        //     const pngUrl = immoDocsLogo

        //     const pngImage = await pdfDoc.embedPng(pngUrl)

        //     page.drawImage(pngImage, {
        //       x: width / 2 - 50,
        //       y: height / 2 - 50,
        //       width: pngImage.width,
        //       height: pngImage.height,
        //       opacity: 0.5,
        //     })
        //   }

        //   const pdfBytes = await pdfDoc.saveAsBase64()
        //   base64Data = `data:application/pdf;base64,${pdfBytes}`
        // }
        return base64Data
      }else {
        throw new Error('No template')
      }
    } catch (err) {
      console.log(err)
    }
  }

  // Download document
  const downloadDocument = async (extension, doc, options = {}) => {
    options.clauses = clauses
    let events = [eventTypeForAction('download')]
    events.push(extension === 'docx' ? EVENT_TYPES.DOCUMENT_DOWNLOAD_DOCX : EVENT_TYPES.DOCUMENT_DOWNLOAD_PDF)
    log_event(events, partner)
    try {
      let tmplt = getTemplateById(doc.template)
      if(!tmplt.sections) {
        tmplt = await getSingleTemplate(doc.template, true)
      }
      if(tmplt && Object.keys(tmplt).length > 0) {
        let documentData = await fetch_document_data(tmplt, doc, extension, agency, user, options, integration)
        if(!documentData) throw new Error('No data')

        if(isIOS && extension === 'pdf') {
          const blob = base64toBlob(documentData)
          const a = document.createElement('a')
          a.onclick = saveAs(blob, `${doc.name || 'document'}.pdf`)
        }else {
          const a = document.createElement("a")
          a.href = `data:${mimeTypeForExtension(extension)};base64,${documentData}`
          a.download = `${doc.name || 'document'}.${extension}`
          a.click()
        }
      }else {
        throw new Error('No template')
      }
    } catch (err) {
      console.log(err)
    }
  }

  const downloadSharedDocument = async (doc) => {
    try {
      const res = await download_shared_document(doc.id)
      const zipUrl = res.url

      const a = document.createElement("a")
      a.href = zipUrl
      a.download = `${doc.name || 'document'}.zip`
      a.click()

    } catch (err) {
      setNotification({ msg: 'Une erreur est survenue, merci de réessayer', type: 'danger' })
      console.error(err)
    }
  }

  // Move document to folder
  const moveDocToFolder = async (document, folder) => {
    let doc = {...document}
    if(!document.folderId) {
      doc = {...document, folderId: []}
    }
    try {
      if(folder) {
        if(!doc.folderId.includes(folder.id)) {
          setShowGlobalResponseLoader(true)
          setGlobalResponseLoaderText('Déplacement du document')
          await updateDocument({ folderId: [folder.id] }, doc)
          setNotification({ msg: `Le document "${doc.name}" a été déplacé dans le dossier "${folder.name}"!`, type: 'default' })
        }else {
          setNotification({ msg: `Le document "${doc.name}" est déjà dans le dossier "${folder.name}"!`, type: 'warning' })
        }
      }else if(doc.folderId.length > 0){
        setShowGlobalResponseLoader(true)
        setGlobalResponseLoaderText('Déplacement du document')
        await updateDocument({ folderId: [] }, doc)
        setNotification({ msg: `Le document "${doc.name}" a été déplacé dans le dossier racine!`, type: 'default' })
      }
    } catch (err) {

    } finally {
      setShowGlobalResponseLoader(false)
      setGlobalResponseLoaderText('')
    }
  }

  // Move to root folder
  const moveDocToRootFolder = async (data) => {
    let doc = {...data}
    if(!doc.folderId) {
      doc = {...data, folderId: []}
    }

    if(doc.folderId.length === 0)
      return setNotification({ msg: `Impossible de déplacer le document "${doc.name}" vers le dossier racine`, type: 'warning' })

    setShowGlobalResponseLoader(true)
    setGlobalResponseLoaderText('Déplacement du document')
    try {
      await updateDocument({ folderId: [] }, doc)
      setNotification({ msg: `Le document "${doc.name}" a été déplacé dans le dossier racine!`, type: 'default' })
    } catch (err) {

    } finally {
      setShowGlobalResponseLoader(false)
      setGlobalResponseLoaderText('')
    }
  }

  // Create document
  const createDocumentFromTemplate = async (tmplt, isStandard = false, values) => {
    let data = {
      values: {},
      checkboxValues: {},
      name: tmplt.name,
      status: 'draft',
      template: tmplt.id,
      progress: 0,
      folderId: [],
      template_category: sections.find(s => s.templates?.includes(tmplt.id))?.id || '',
    }
    if(isStandard) {
      data.values = tmplt.values || {}
      data.template = tmplt.template
      data.content_changes = tmplt.content_changes || []
      data.content_editable = tmplt.content_editable || false
      data.standard_template = tmplt.id
      data.standard_template_folder = tmplt.folderId[0] || ''
      data.template_category = sections.find(s => s.templates?.includes(tmplt.template))?.id || ''
      data.checkboxValues = tmplt.checkboxValues || {}
      data.attachments = tmplt.attachments || []
      data.use_watermark = tmplt.use_watermark || false
      data.font_size = tmplt.font_size || 'normal'
      data.empty_lines_added = tmplt.empty_lines_added || false
    }
    if(tmplt.custom_cover) {
      data.custom_cover = tmplt.custom_cover
    }
    if(values) {
      data.values = values
    }
    try {
      const id = await createDocument(data)
      return id
    } catch (err) {
      setNotification({ msg: 'Une erreur est survenue, merci de réessayer', type: 'danger' })
    }
  }

  // Share document
  // const shareDocument = async (data, doc, template, attachments) => {
  //   const { message, email, recipients, subject, sentEmailId, addWatermark } = data

  //   try {
  //     if(!template.sections) {
  //       template = await getSingleTemplate(doc.template, true)
  //     }
  //     const documentData = await fetch_document_data(template, doc, 'pdf', agency, user, {}, integration)
  //     const emails = recipients.map(r => r.value)
  //     await sendEmail(email, emails, subject, message, doc.name, documentData, [])
  //     const sentEmails = doc.sentEmails ? [...doc.sentEmails] : []
  //     sentEmails.push({
  //       id: sentEmailId,
  //       sender: email,
  //       attachments: attachments,
  //       watermark: addWatermark,
  //     });
  //     await updateDocument({ sentEmails }, {...doc})
  //     setNotification({ msg: 'Message envoyé !', type: 'success' })
  //   } catch (err) {
  //     setNotification({ msg: 'Une erreur est survenue, merci de réessayer', type: 'danger' })
  //   }
  // }
  const shareDocumentWithContact = async (documentId, data) => {
    const res = await post_request(`${baseUrl}/document/${documentId}/share_single_contact`, data)
    console.log('response from share_single_contact:', res)
    refreshDocument(documentId)
    if(res.error) {
      throw new Error(res.error)
    }
  }

  const removeDocumentShareWithContact = async (documentId, contact) => {
    const res = await post_request(`${baseUrl}/document/${documentId}/remove_share_single_contact`, { contact })
    console.log('response from remove_share_single_contact:', res)
    refreshDocument(documentId)
    if(res.error) {
      throw new Error(res.error)
    }
  }

  const shareDocumentWithValidation = async (documentId, data) => {

    try {
      // if(!template.sections) {
      //   template = await getSingleTemplate(doc.template, true)
      // }
      
      await post_request(`${baseUrl}/document/${documentId}/share`, data)
      // const documentData = await fetch_document_data(template, doc, 'pdf', agency, user, {}, partner)
      // const emails = recipients.map(r => r.value)
      // await sendEmail(email, emails, subject, message, doc.name, documentData, [])
      // const sentEmails = doc.sentEmails ? [...doc.sentEmails] : []
      // sentEmails.push({
      //   id: sentEmailId,
      //   sender: email,
      //   attachments: attachments,
      //   watermark: addWatermark,
      // });
      // await updateDocument({ sentEmails }, {...doc})
      setNotification({ msg: 'Message envoyé !', type: 'success' })
    } catch (err) {
      setNotification({ msg: 'Une erreur est survenue, merci de réessayer', type: 'danger' })
    }
  }

  // Move document to deleted documents
  const moveDocumentToDeleteDocuments = async (doc) => {
    try {
      await updateDocument({ deleted: true, archived: false }, doc)
      temporarilyDeleteMultipleDocuments([doc])
    } catch (err) {
      console.log(err)
    }
  }

  // Duplicate document
  const duplicateDocument = async ({doc, linesAddedTo, template}) => {
    let docCopy = {
      name: `Copie de ${doc.name}`,
      progress: doc.progress || 0,
      status: doc.status,
      template: doc.template,
      values: doc.values
    }
    try {
      const id = await createDocument(docCopy)
      return id
    } catch (err) {
      console.log(err)
    }
  }

  // Download document data
  const downloadDocumentData = ({ documentValues, documentCheckboxValues, documentName, documentId }) => {
    let data = { ...documentValues }
    if(documentCheckboxValues) {
      data = {
        ...data,
        ...documentCheckboxValues
      }
    }
    const buffer = Buffer.from(JSON.stringify(data, null, 2), 'ascii')
    const base64Url = `data:application/json;base64,${buffer.toString('base64')}`
    const a = document.createElement("a")
    a.href = base64Url
    a.download = `${documentName || 'document'}${documentId ? `(${documentId})` : ''}_data.json`
    a.click()
  }

  // Upload to cloud
  const uploadToCloud = async ({ template, doc, immocloudConfig }) => {
    if(!immocloudConfig.handle || !immocloudConfig.id || !immocloudConfig.site_id) {
      return alert('Aucun bien n’est lié à ce document. Le document doit être créé depuis votre logiciel de transaction')
    }
    if(!template.sections) {
      template = await getSingleTemplate(doc.template, true)
    }
    const fileBase64 = await fetch_document_data(template, doc, 'pdf', agency, user, {}, integration)
    const response = await push_to_immocloud(fileBase64, `${doc.name}.pdf`, immocloudConfig.site_id, immocloudConfig.handle, immocloudConfig.id)
    if(response.error) {
      let message = ''
      if(response.error.error && response.error.error.product_id) {
        message = 'Aucun bien n’est lié à ce document. Le document doit être créé depuis votre logiciel de transaction'
      } else if(response.error.error) {
        for(let key in response.error.error) {
          if(typeof response.error.error[key] === 'object') {
            for(let i in response.error.error[key]) {
              if(message) {
                message += '\n'
              }
              message += response.error.error[key][i]
            }
          } else if(typeof response.error.error[key] === 'string') {
            if(message) {
              message += '\n'
            }
            message += response.error.error[key]
          }
        }
      }
      if(typeof message !== 'string' || !message.trim()) {
        message = 'Aucun bien n’est lié à ce document. Le document doit être créé depuis votre logiciel de transaction'
      }
      alert(message)
    } else {
      alert("Le document a été téléchargé avec succès")
    }
  }

  /* ===== Helpers ===== */
  // Extract variable types from sections
  const extractVariableTypesFromSections = (sections, documentValues) => {
    let variablesWithTypes = extractVariablesWithTypesFromSections(sections, documentValues)
    let types = {}
    for(let i in variablesWithTypes) {
      types[variablesWithTypes[i].variable] = variablesWithTypes[i].type
    }
    return types
  }

  // Extract variebls with types from sections
  const extractVariablesWithTypesFromSections = (sections, documentValues) => {
    let variablesWithTypes = []
    for(let i in sections) {
      let section = sections[i]
      if(!areSectionConditionsMet(section, documentValues)) {
        continue
      }
      if(section.sections) {
        let vars = extractVariablesWithTypesFromSections(section.sections)
        for(let i in vars) {
          variablesWithTypes.push(vars[i])
        }
      }
      if(section.type === 'question') {
        if(isCoverPageVariable(section.variable)) {
          continue
        }
        variablesWithTypes.push({variable: section.variable, type: section.data_type})
      } if(section.type === 'text' || section.type === 'heading1' || section.type === 'heading2' || section.type === 'heading3') { // todo make more general
        for(let vi in section.variables) {
          if(isCoverPageVariable(section.variables[vi].variable)) {
            continue
          }
          variablesWithTypes.push({variable: section.variables[vi].variable, type: section.variables[vi].type})
        }
      }
    }
    let uniqueVariables = [...new Set(variablesWithTypes)];
    return uniqueVariables
  }

  // Join values
  const joinValues = (custom, prefill) => {
    let values = {...custom}
    for(let key in prefill) {
      if(!values[key]) {
        values[key] = prefill[key]
      }
    }
    return values
  }

  // Remove/clear values with lines
  const clearLinesFromValues = ({ documentValues, linesAddedTo, setLinesAdded, setLinesAddedTo }) => {
    let docValues = {...documentValues}
    if(linesAddedTo.length > 0) { // don't save underscores to db, remove them from values before saving
      linesAddedTo.forEach(v => {
        if(v.belongs_to) {
          if(docValues[v.belongs_to]) {
            let repeatable = JSON.parse(JSON.stringify(docValues[v.belongs_to]))
            if(repeatable[v.index][v.variable]) {
              delete repeatable[v.index][v.variable]
            }
            docValues[v.belongs_to] = repeatable
          }
        }else {
          if(docValues[v.variable]) {
            delete docValues[v.variable]
          }
        }
      })
    }
    setLinesAdded(false)
    setLinesAddedTo([])
    return docValues
  }

  // Get progress
  const progress = ({ templateObject, documentValues, linesAddedTo }) => {
    let p = 0;
    if(!templateObject.sections) {
      return p
    }
    let variableKeys = activeVariableKeys(templateObject, documentValues)
    if(variableKeys) {
      let totalVariables = variableKeys.length

      for(let i in variableKeys) {
        if(typeof variableKeys[i] === 'string') {
          if(!!documentValues[variableKeys[i]] && !linesAddedTo.find(v => v.variable === variableKeys[i])) {
            p += 1 / variableKeys.length
          }
        }else {
          if(documentValues[variableKeys[i].belongs_to] && !!documentValues[variableKeys[i].belongs_to][variableKeys[i].index][variableKeys[i].variable] && !linesAddedTo.find(v => v.variable === variableKeys[i].variable)) {
            p += 1 / variableKeys.length
          }
        }
      }
    }
    return p
  }

  // Active variable keys
  const activeVariableKeys = (templateObject, documentValues) => {
    let templateCopy = {...templateObject}
    let sections = [...templateCopy.sections]
    if(templateCopy.footer) {
      sections.push({...templateCopy.footer, type: templateCopy.footer.type || 'text'})
    }
    let vars = extractVariablesFromSections(documentValues, sections)
    const excludedProgressVars = [...CONTACT_ID_VARIABLES]
    vars = vars.filter(v => !excludedProgressVars.includes(v))
    return vars
  }

  // Extract variables from sections
  const extractVariablesFromSections = (documentValues, sections, active = true, full = false) => {
    let variables = []
    for(let i in sections) {
      let section = sections[i]
      if(active && (section.condition || section.conditions) && !areSectionConditionsMet(section, documentValues)) {
        continue
      }
      if(section.sections) {
        let vars = extractVariablesFromSections(section.sections)
        for(let i in vars) {
          variables.push(vars[i])
        }
      }
      if(section.type === 'question') {
        if(isCoverPageVariable(section.variable)) {
          continue
        }
        if(full) {
          variables.push({ variable: section.variable, type: section.data_type, options: section.options })
        } else {
          variables.push(section.variable)
        }
      } if(section.type === 'text' || section.type === 'heading1' || section.type === 'heading2' || section.type === 'heading3') { // todo make more general
        for(let vi in section.variables) {
          if(isCoverPageVariable(section.variables[vi].variable)) {
            continue
          }
          if(full) {
            // variables.push(section.variables[vi])
            if(section.repeatable_section_id && documentValues[section.repeatable_section_id]) {
              documentValues[section.repeatable_section_id].forEach((item, index) => {
                let splittedVar = section.variables[vi].variable.split('_')
                let lastEl = splittedVar[splittedVar.length - 1]
                if(lastEl !== 'id') {
                  variables.push({...section.variables[vi], index, belongs_to: section.repeatable_section_id})
                }
              })
            }else {
              if(section.repeatable_section_id) {
                variables.push({...section.variables[vi], belongs_to: section.repeatable_section_id, index: 0 })
              }else {
                variables.push(section.variables[vi])
              }
            }
          } else {
            if(section.repeatable_section_id && documentValues[section.repeatable_section_id]) {
              documentValues[section.repeatable_section_id].forEach((item, index) => {
                let splittedVar = section.variables[vi].variable.split('_')
                let lastEl = splittedVar[splittedVar.length - 1]
                if(lastEl !== 'id') {
                  variables.push({variable: section.variables[vi].variable, index, belongs_to: section.repeatable_section_id})
                }
              })
            }else {
              if(section.repeatable_section_id) {
                variables.push({...section.variables[vi].variable, belongs_to: section.repeatable_section_id, index: 0})
              }else {
                variables.push(section.variables[vi].variable)
              }
            }
          }
        }
      } else if(section.type === 'bullet_list') {
        for(let bi in section.items) {
          if(section.repeatable_section_id && documentValues[section.repeatable_section_id]) {
            documentValues[section.repeatable_section_id].forEach((item, index) => {
              for(let vi in section.items[bi].variables) {
                if(isCoverPageVariable(section.items[bi].variables[vi].variable)) {
                  continue
                }
                if(full) {
                  variables.push({...section.items[bi].variables[vi], index, belongs_to: section.repeatable_section_id})
                } else {
                  variables.push({ variable: section.items[bi].variables[vi].variable, index, belongs_to: section.repeatable_section_id })
                }
              }
            })
          } else {
            for(let vi in section.items[bi].variables) {
              if(isCoverPageVariable(section.items[bi].variables[vi].variable)) {
                continue
              }
              if(full) {
                variables.push(section.items[bi].variables[vi])
              } else {
                variables.push(section.items[bi].variables[vi].variable)
              }
            }
          }
        }
      } else if(section.type === 'table') {
        const tableRepeatableId = section.repeatableRowId
        const tableValues = documentValues[tableRepeatableId] || []
        for(let ti in tableValues) {
          for(let bi in section.row) {
            for(let vi in section.row[bi].variables) {
              if(isCoverPageVariable(section.row[bi].variables[vi].variable)) {
                continue
              }
              if(full) {
                variables.push({...section.row[bi].variables[vi], index: ti, belongs_to: tableRepeatableId})
              } else {
                variables.push({ variable: section.row[bi].variables[vi].variable, index: ti, belongs_to: tableRepeatableId })
              }
            }
          }
        }
      }
    }
    let uniqueVariables = [...new Set(variables)];
    return uniqueVariables
  }

  // Mime type for extension
  const mimeTypeForExtension = (extension) => {
    switch(extension) {
      case 'pdf':
        return 'application/pdf'
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      default:
        return ''
    }
  }

  // Get all variables
  const allVariables = (documentValues, tempData, full = true, includeFooter = false) => {
    if(!tempData) {
      return null
    }
    let sections = tempData.sections
    if(includeFooter && tempData.footer) {
      sections.push({...tempData.footer, type: tempData.footer.type || 'text'})
    }
    return extractVariablesFromSections(documentValues, sections, false, full)
  }

  // Get key name
  const getKeyName = (key) => {
    let names = ['firstname', 'lastname', 'telephone', 'email_address']
    let keyName = ''
    for(let i = 0; i < names.length; i++) {
      if(key.includes(names[i])) {
        keyName = names[i]
        break
      }
    }

    return keyName
  }

  // Get key without prop name
  const getKeyWithoutPropName = (arr) => {
    let names = ['firstname', 'lastname', 'telephone', 'email']
    let filtered = []
    for(let i = 0; i < names.length; i++) {
      if(arr.includes(names[i])) {
        if(names[i] === 'email') {
          filtered = arr.filter(item => item !== 'email' && item !== 'address')
        }else {
          filtered = arr.filter(item => item !== names[i])
        }
        break
      }
    }

    return filtered.length > 1 ? filtered.join('_') : filtered.join('')
  }

  // extract customers from data using custom variable matching
  /** example variable matching
   *  {
   *    "surety": {
   *      "firstname": "ac3_prenom_caution",
   *      "lastname": "ac3_nom_caution"
   *    },
   *    "tenant": {
   *      "firstname": "ac3_prenom_locataire",
   *      "lastname": "ac3_nom_locataire"
   *    }
   *  }
   */

  const extractCustomersFromDataWithCustomMatching = (data, matching, prefixOrder, options = {}) => {
    if(!data) return []
    if(!matching) return []

    const shouldFilterByTemplate = Boolean(options.template) && !options.skipActiveVariableFilter
    const sourceData = shouldFilterByTemplate
      ? filteredDocumentValues(data, options.template, { clauses })
      : data
    const dataClone = { ...sourceData }
    let contacts = []
    const contactsPerPrefix = {}
    for(let variableKey in dataClone) {
      const value = dataClone[variableKey]
      if(Array.isArray(value)) {
        for(let i in value) {
          contacts.push(...extractCustomersFromDataWithCustomMatching(value[i], matching, prefixOrder, {
            ...options,
            skipActiveVariableFilter: true,
          }))
        }
        continue
      }
      for(let contactType in matching) {
        const variableKeys = Object.keys(matching[contactType])
        if(variableKeys.includes(variableKey)) {
          let prefix = DOC_CONTACT_PREFIXES.find(prefix => variableKey.includes(prefix))
          if(!prefix) {
            prefix = contactType
          }
          if(!contactsPerPrefix[prefix]) {
            contactsPerPrefix[prefix] = {
              category: contactType
            }
          }
          const prefixesOrder = prefixOrder?.[contactType]
          let prefixOrderIndex = -1
          if(prefixesOrder) {
            for(let i = 0; i < prefixesOrder.length; i++) {
              if(variableKey.startsWith(prefixesOrder[i])) {
                prefixOrderIndex = i
                break
              }
            }
          }
          contactsPerPrefix[prefix].order = prefixOrderIndex
          const matchingKey = matching[contactType][variableKey]
          contactsPerPrefix[prefix][matchingKey] = value
        }
      }
    }
    const contactsArray = Object.values(contactsPerPrefix)
    contacts.push(...contactsArray)
    const uniqueContacts = []
    for (let contact of contacts) {
      const existingIndex = uniqueContacts.findIndex(c => {
        return Object.keys(c).every(key => contact[key] === c[key])
      })
      if (existingIndex !== -1) {
        uniqueContacts[existingIndex] = {
          ...uniqueContacts[existingIndex],
          ...contact
        }
      } else {
        uniqueContacts.push(contact)
      }
    }
    const sortedUniqueContacts = uniqueContacts.toSorted((a, b) => {
      if(a.category !== b.category) {
        return a.category.localeCompare(b.category)
      }
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order
      } else if (a.order !== undefined) {
        return -1
      } else if (b.order !== undefined) {
        return 1
      }
      return 0
    })
    return sortedUniqueContacts
  }

  const extractCustomersFromData = (data) => {
    if(!data) return []
    let contacts_keys = []
    let dataClone = {...data}
    const additionalContacts = {}
    for(let key in dataClone) {
      const keyArr = key.split('_')
      let keyClone
      if(isNaN(keyArr[keyArr.length - 1])){
        keyClone = key
      }else {
        keyArr.pop()
        keyClone = keyArr.join('_')
      }
      if(DOC_CONTACTS_KEYS.includes(keyClone)) {
        contacts_keys.push(key)
        continue
      }

      const cautionMatch = key.match(/^(ac3)_(prenom|nom)_caution(_pm)?(?:_(\d+))?$/)
      if(cautionMatch) {
        const [, prefix, fieldName, isPm, index] = cautionMatch
        const contactKey = `${prefix}_caution${isPm || ''}${index ? `_${index}` : ''}`
        if(!additionalContacts[contactKey]) {
          additionalContacts[contactKey] = {
            belongs_to: contactKey
          }
        }
        additionalContacts[contactKey][fieldName === 'nom' ? 'firstname' : 'lastname'] = dataClone[key]
      }
    }
    let contacts = []
    contacts_keys.forEach((key, idx) => {
      const keyArr = key.split('_')
      let keyWithoutPropName = getKeyWithoutPropName(keyArr)
      let keyName = getKeyName(key)
      if(idx === 0) {
        contacts[0] = {
          [keyName]: dataClone[key],
          belongs_to: keyWithoutPropName
        }
      }else {
        let objFound = contacts.find(c => c.belongs_to === keyWithoutPropName)
        if(contacts.find(c => c.belongs_to === keyWithoutPropName)) {
          objFound[keyName] = dataClone[key]
        }else {
          contacts.push({ [keyName]: dataClone[key], belongs_to: keyWithoutPropName })
        }
      }
    })
    contacts.push(...Object.values(additionalContacts))
    return contacts
  }

  // Get customers from data
  const getCustomersFromData = (data, setDocumentContacts) => {
    if(!data) return []
    let contacts_keys = []
    let dataClone = {...data}
    const arrayContacts = []
    for(let key in dataClone) {
      const keyArr = key.split('_')
      let keyClone
      if(Array.isArray(dataClone[key])) {
        for(let i in dataClone[key]) {
          arrayContacts.push(...extractCustomersFromData(dataClone[key][i]))
        }
        continue
      }
      if(isNaN(keyArr[keyArr.length - 1])){
        keyClone = key
      }else {
        keyArr.pop()
        keyClone = keyArr.join('_')
      }
      if(DOC_CONTACTS_KEYS.includes(keyClone)) {
        contacts_keys.push(key)
      }
    }
    let contacts = []
    contacts_keys.forEach((key, idx) => {
      const keyArr = key.split('_')
      let keyWithoutPropName = getKeyWithoutPropName(keyArr)
      let keyName = getKeyName(key)
      if(idx === 0) {
        contacts[0] = {
          [keyName]: dataClone[key],
          belongs_to: keyWithoutPropName
        }
      }else {
        let objFound = contacts.find(c => c.belongs_to === keyWithoutPropName)
        if(contacts.find(c => c.belongs_to === keyWithoutPropName)) {
          objFound[keyName] = dataClone[key]
        }else {
          contacts.push({ [keyName]: dataClone[key], belongs_to: keyWithoutPropName })
        }
      }
    })
    const uniqueContacts = [...new Set([...contacts, ...arrayContacts])]
    setDocumentContacts(uniqueContacts)
    return uniqueContacts
  }

  const dataKeyArrayForType = (type) => {
    return CONTACT_DATA_KEYS[type]
  }

  const extractCustomerData = (data, set = true) => {
    let types = Object.keys(CONTACT_DATA_KEYS)
    let contacts = {}
    for(let t in types) {
      let type = types[t]
      contacts[type] = []
      let sourceKeys = dataKeyArrayForType(type)
      for(let key in data) {
        if(Array.isArray(data[key])) {
          // extract customers from each array item
          for(let i in data[key]) {
            const customerDataFromItem = extractCustomerData(data[key][i], false)
            if(customerDataFromItem && customerDataFromItem[type] && customerDataFromItem[type].length > 0) {
              contacts[type].push(...customerDataFromItem[type])
            }
          }
          continue
        }
        let isCustomerKey = false
        let keyWithoutIndex
        let components = key.split('_')
        let customerIndex = components[components.length - 1]
        if(isNaN(customerIndex)) {
          keyWithoutIndex = key
          customerIndex = 0
        } else {
          components.pop()
          keyWithoutIndex = components.join('_')
          customerIndex--
        }

        for(let i in sourceKeys) {
          if(keyWithoutIndex === sourceKeys[i]) {
            isCustomerKey = true
            break
          }
        }
        if(isCustomerKey) {
          if(!contacts[type][customerIndex]) {
            contacts[type][customerIndex] = {}
          }
          contacts[type][customerIndex][keyWithoutIndex] = data[key]
        }
      }
    }
    if(set) {
      const uniqueContacts = {}
      for(let t in types) {
        let type = types[t]
        uniqueContacts[type] = []
        for(let c in contacts[type]) {
          const contact = contacts[type][c]
          const existingIndex = uniqueContacts[type].findIndex(uc => {
            return Object.keys(contact).every(key => contact[key] === uc[key])
          })
          if (existingIndex !== -1) {
            uniqueContacts[type][existingIndex] = {
              ...uniqueContacts[type][existingIndex],
              ...contact
            }
          } else {
            uniqueContacts[type].push(contact)
          }
        }
      }
      setContacts(uniqueContacts)
    }
    return contacts
  }

  // Check if cloud upload is available
  const immocloudUploadAvailable = () => {
    // const availablePartners = ['jurisur', 'squarehabitat', 'cai', 'cote-particulier']
    const unavailablePartners = ['kel']
    return !unavailablePartners.includes(partner)
  }
  /* ===== END Helpers ===== */

  // const renderDocument = async (document, options, extension) => {
  //   console.log("render document", document, options, extension)
  //   const template = await getTemplateById(document.template)
  //   const sectionsWithClauses = []
  //   template.sections.forEach((section) => {
  //     if(section.type !== 'clause') {
  //       sectionsWithClauses.push(section)
  //       return
  //     }
  //     const clause = clauses[section.clause_id]
  //     if(!clause) {
  //       return
  //     }
  //     sectionsWithClauses.push(...(clause.sections.map((clauseItem) => ({
  //       ...clauseItem,
  //       repeatable_section_ids: [...(section.repeatable_section_ids || []), ...(clauseItem.repeatable_section_ids || [])],
  //     }))))            
  //   })
  //   template.sections = sectionsWithClauses
  //   let cpConfig = coverPageConfig(document.custom_cover, agency, user, document.name, template.hasCoverPage, integration)
  //   // let values = {...document.values, ...cpConfig.coverPageVariables}
  //   const documentFormulas = getTemplateFormulas(template)
  //   const filteredValues = filteredDocumentValues(document.values, template)
  //   const formulaResults = insertFormulaResults(filteredValues, documentFormulas)
  //   let emptyLinesValues = {}
  //   if(document.empty_lines_added) {
  //     emptyLinesValues = emptyLinesValuesForTemplate(template, document.values)
  //   }
  //   let values = {
  //     ...filteredValues,
  //     ...formulaResults,
  //     ...cpConfig.coverPageVariables,
  //     ...emptyLinesValues
  //   }
  //   const images = {...cpConfig.coverPageImages, ...(document.images || {})}
  //   const completeOptions = mergeDeep(options, {...cpConfig.coverPageOptions, checkboxValues: document.checkboxValues})

  //   console.log("render document with data", )

  //   let doc = await generate_document(template, values, extension, images, completeOptions)
  //   if(doc.error) {
  //     return null
  //   }
  //   return doc.data
  // }

  return {
    getDocumentHistory,
    getDocumentEventsDayArray,
    extractVariableTypesFromSections,
    joinValues,
    getPreviewData,
    clearLinesFromValues,
    progress,
    mimeTypeForExtension,
    downloadDocument,
    allVariables,
    moveDocToFolder,
    moveDocToRootFolder,
    createDocumentFromTemplate,
    // shareDocument,
    getCustomersFromData,
    moveDocumentToDeleteDocuments,
    duplicateDocument,
    downloadDocumentData,
    immocloudUploadAvailable,
    uploadToCloud,
    extractCustomerData,
    extractCustomersFromDataWithCustomMatching,
    // shareDocumentWithValidation
    shareDocumentWithContact,
    removeDocumentShareWithContact,
    downloadSharedDocument,
  }
}

export default useSingleDocumentActions
